import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, ArrowRightLeft, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

interface TransferForm {
  recipient_user_id: string;
  points: string; // ONLY POINTS for Agent-to-Agent
  reference: string;
}

export function AgentToAgentTransferPage() {
  const [recipient, setRecipient] = useState<any | null>(null);
  const [searching, setSearching] = useState(false);
  const qc = useQueryClient();

  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm<TransferForm>();
  const recipientIdValue = watch("recipient_user_id");

  // Live Search for Recipient Agent
  useEffect(() => {
    if (!recipientIdValue || recipientIdValue.length < 5) {
      setRecipient(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await get<any>(`/agent/users/search?user_id=${recipientIdValue}`);
        setRecipient(res);
      } catch {
        setRecipient(null);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [recipientIdValue]);

  const transfer = useMutation({
    mutationFn: (d: TransferForm) =>
      post<any>("/agent/points/agent-transfer", {
        recipient_user_id: d.recipient_user_id,
        points: parseInt(d.points), // Send exactly the points entered
        reference: d.reference,
      }, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => {
      toast.success("Points successfully transferred to Agent!");
      reset();
      setRecipient(null);
      qc.invalidateQueries({ queryKey: ["agent-profile"] });
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Agent to Agent Transfer</h1>
        <p className="text-sm text-surface-500 mt-1">
          Transfer points directly to another verified Agent.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Transfer Details</CardTitle>
          <ArrowRightLeft className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit((d) => transfer.mutate(d))} className="space-y-6">
            
            <div className="space-y-3">
              <Input
                label="Recipient Agent ID"
                placeholder="Enter Agent's User ID (e.g., USR000002)"
                leftIcon={<Search className="h-4 w-4" />}
                error={errors.recipient_user_id?.message}
                {...register("recipient_user_id", { required: "Recipient ID is required" })}
              />

              <div className="min-h-[60px]">
                {searching ? (
                  <div className="text-sm text-surface-500 animate-pulse">Searching agent...</div>
                ) : recipient ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-success-200 bg-success-50">
                    <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                    <div>
                      <div className="text-sm font-bold text-success-900">{recipient.full_name}</div>
                      <div className="text-xs text-success-700">Verified Agent Account</div>
                    </div>
                  </div>
                ) : recipientIdValue && recipientIdValue.length >= 5 ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-danger-200 bg-danger-50">
                    <ShieldAlert className="h-5 w-5 text-danger-600 shrink-0" />
                    <div className="text-sm text-danger-900">Agent not found or inactive.</div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Point Input */}
            <Input
              label="Points to Transfer"
              type="number"
              min="1"
              placeholder="Enter exact points"
              error={errors.points?.message}
              {...register("points", { 
                required: "Points are required",
                validate: (val) => Number(val) > 0 || "Must be positive"
              })}
            />

            <Input
              label="Reference / Note (Optional)"
              placeholder="e.g. Stock adjustment for August"
              {...register("reference")}
            />

            <Button 
              type="submit" 
              size="lg" 
              fullWidth 
              loading={transfer.isPending}
              disabled={!recipient}
            >
              Confirm Point Transfer
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}